// Extracted UserRow component and AdminUser interface from AdminUsersPage.

import { useState } from "react";
import type { JSX } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { adminFetch } from "../lib/api.js";

export interface AdminUser {
  _id:       string;
  email:     string;
  role:      string;
  tenant_id: string;
  is_active: boolean;
}

// Canonical assignable user roles — matches the UserRole enum in apps/api
// (db/models/user.ts). Drives the role-edit dropdown here AND the Role filter on
// AdminUsersPage. (Replaces the older ["admin","viewer"] labels, which were not
// real role values and silently fell through to the grey badge.)
export const USER_ROLES = ["agency_admin", "coordinator", "carer", "super_admin", "view_only"] as const;

const ROLE_BADGE: Record<string, string> = {
  agency_admin: "bg-indigo-100 text-indigo-700",
  coordinator:  "bg-teal-100 text-teal-700",
  carer:        "bg-blue-100 text-blue-700",
  super_admin:  "bg-purple-100 text-purple-700",
  view_only:    "bg-slate-100 text-slate-600",
};

function roleBadgeCls(role: string): string {
  return ROLE_BADGE[role] ?? "bg-slate-100 text-slate-600";
}

const QK_USERS = "admin-users" as const;

// eslint-disable-next-line max-lines-per-function -- row markup: email/role/tenant/status + two actions
export function UserRow({ user, tenantName, onResetMfa }: {
  user:        AdminUser;
  tenantName?: string;
  onResetMfa:  (user: AdminUser) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [editingRole, setEditingRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState(user.role);

  async function toggleActive(): Promise<void> {
    const action = user.is_active ? "deactivate" : "activate";
    await adminFetch(`/admin/users/${user._id}/${action}`, { method: "PATCH" });
    void qc.invalidateQueries({ queryKey: [QK_USERS] });
  }

  async function saveRole(): Promise<void> {
    await adminFetch(`/admin/users/${user._id}/role`, {
      method: "PATCH",
      body:   JSON.stringify({ role: selectedRole }),
    });
    setEditingRole(false);
    void qc.invalidateQueries({ queryKey: [QK_USERS] });
  }

  return (
    <tr className="hover:bg-slate-50 transition-colors text-sm">
      <td className="py-3 px-4 text-slate-700">{user.email}</td>
      <td className="py-3 pr-4">
        {editingRole ? (
          <div className="flex items-center gap-1">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="rounded border border-slate-300 px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {USER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button
              onClick={() => { void saveRole(); }}
              className="px-2 py-0.5 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Save
            </button>
            <button
              onClick={() => { setEditingRole(false); setSelectedRole(user.role); }}
              className="px-2 py-0.5 text-xs rounded bg-slate-200 text-slate-600 hover:bg-slate-300"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button onClick={() => setEditingRole(true)} title="Click to change role">
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer ${roleBadgeCls(user.role)}`}>
              {user.role}
            </span>
          </button>
        )}
      </td>
      <td className="py-3 pr-4 text-slate-600" title={user.tenant_id}>
        {tenantName ?? <span className="font-mono text-xs text-slate-400">{user.tenant_id.slice(-8)}</span>}
      </td>
      <td className="py-3 pr-4">
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
          user.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
        }`}>
          {user.is_active ? "Active" : "Inactive"}
        </span>
      </td>
      <td className="py-3 pr-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => { void toggleActive(); }}
            className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${
              user.is_active
                ? "bg-red-50 text-red-700 hover:bg-red-100"
                : "bg-green-50 text-green-700 hover:bg-green-100"
            }`}
          >
            {user.is_active ? "Deactivate" : "Activate"}
          </button>
          <button
            onClick={() => onResetMfa(user)}
            title="Clear this user's TOTP — they'll re-enroll on next login"
            className="px-3 py-1 text-xs rounded-md font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
          >
            Reset MFA
          </button>
        </div>
      </td>
    </tr>
  );
}
